import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertBanner,
  Spinner,
  Tabs,
  TagGeneral,
  Text,
} from '@spark';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { GlobalContext } from '@/app/providers/globalState';
import { getTaskTypeLabel, TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { FINAL_TRAINING_STATUSES } from '@/features/trainings/constants';
import TrainingProgress from './CurrentTraining/TrainingProgress';
import TrainingStatus from './CurrentTraining/TrainingStatus';
import { fetchModelDetails, updateModelNotes } from '../api/modelDetailsApi';
import { shouldShowTrainingSubObjective } from './trainingsUtils';
import { optimizeOnDisplay } from '../utils/modelDetailsOptimizeOn';
import ModelDetailsCodeSection from './ModelDetailsCodeSection';
import ModelDetailsTrainingRunInfrastructure from './ModelDetailsTrainingRunInfrastructure';
import {
  getTrainedPredictionStrategyDisplayLabel,
  isVllmTaskType,
} from '@/features/deployments/components/configureDeployment/taskRuntimeDefaults';
import ModelDetailsDataTab from './ModelDetailsDataTab';
import ModelDetailsBenchmarksTab from './ModelDetailsBenchmarksTab';
import VersionSummarySection from './VersionSummarySection';
import SideTabsLayout from '@/shared/components/SideTabsLayout';
import '@/app/styles/scrollbar.css';

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? '');
  }
}

function formatDurationMs(ms) {
  const totalSec = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  if (h <= 0 && m <= 0) return '-';
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

const MODEL_DETAILS_TAB_ITEMS = [
  { id: 'summary', label: 'Summary' },
  { id: 'benchmarks', label: 'Benchmarks' },
  { id: 'data', label: 'Data' },
  { id: 'training', label: 'Training' },
];

/**
 * Hosts the Summary / Benchmarks / Data / Training tabs either as the shared
 * vertical side rail (task page) or as the classic horizontal tabs (drawer).
 */
function ModelDetailsTabsRegion({ sideTabs, activeId, onChange, children }) {
  if (sideTabs) {
    return (
      <SideTabsLayout items={MODEL_DETAILS_TAB_ITEMS} activeId={activeId} onChange={onChange}>
        {children}
      </SideTabsLayout>
    );
  }
  return (
    <>
      <Tabs activeId={activeId} onChange={onChange} items={MODEL_DETAILS_TAB_ITEMS} />
      <div style={{ paddingTop: 16 }}>{children}</div>
    </>
  );
}

/**
 * Model details body — Summary / Benchmarks / Data / Training tabs.
 *
 * Extracted from `ModelDetailsDrawer` so the same content renders both inside
 * the drawer (Models page, Evaluation) and as the task page "Versions"
 * tab. Owns the `model-details` fetch and the model-notes autosave; the host
 * surface owns any chrome (drawer header, deploy/download actions).
 */
export default function ModelDetailsContent({
  distillationEventId,
  version,
  clientId = DEFAULT_CLIENT_ID,
  role,
  taskType,
  /** Distillation event id of the benchmark currently shown on the main Evaluation page. */
  selectedBenchmarkEventId = null,
  /** Called when the user picks a benchmark row from the Benchmarks tab. */
  onSelectBenchmark,
  onDownloadBenchmark,
  /** Reports the loaded model-details response to the host (e.g. drawer status fallback). */
  onDetailsLoaded,
  /** Render the Summary/Benchmarks/Data/Training tabs as the shared vertical
      side rail (task page) instead of horizontal tabs (drawer default). */
  sideTabs = false,
  /** Extra header content (e.g. the task page version selector) rendered
      inside the tabs region, above the tab body, on every tab. */
  headerExtra = null,
}) {
  const { trainStatuses } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  const [modelNotes, setModelNotes] = useState('');
  const lastSavedNotesRef = useRef('');
  const saveTimerRef = useRef(null);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const onDetailsLoadedRef = useRef(onDetailsLoaded);
  onDetailsLoadedRef.current = onDetailsLoaded;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!distillationEventId) {
        setDetails(null);
        setError(null);
        setLoading(false);
        setActiveTab('summary');
        setModelNotes('');
        lastSavedNotesRef.current = '';
        setNotesSaving(false);
        setNotesError(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const resp = await fetchModelDetails({
          client_id: clientId,
          distillation_event_id: distillationEventId,
        });

        if (cancelled) return;
        if (!resp?.success) {
          setDetails(null);
          setError(resp?.message || 'Failed to load model details.');
          return;
        }

        setDetails(resp);
        onDetailsLoadedRef.current?.(resp);
      } catch (e) {
        if (cancelled) return;
        setDetails(null);
        setError(e?.detail || e?.message || 'Failed to load model details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [distillationEventId, clientId]);

  // Hydrate notes draft from server on model change.
  useEffect(() => {
    const serverNotes = details?.distillation_event?.model_notes ?? '';
    setModelNotes(serverNotes);
    lastSavedNotesRef.current = serverNotes;
    setNotesError(null);
  }, [details?.distillation_event?.id]);

  // Debounced autosave while typing (Editor/Admin only).
  useEffect(() => {
    if (!distillationEventId) return;
    if (role === 'Viewer') return;

    const current = modelNotes ?? '';
    const lastSaved = lastSavedNotesRef.current ?? '';
    if (current === lastSaved) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setNotesError(null);

    let cancelled = false;
    saveTimerRef.current = setTimeout(async () => {
      try {
        setNotesSaving(true);
        const resp = await updateModelNotes({
          client_id: clientId,
          distillation_event_id: distillationEventId,
          model_notes: current,
        });
        if (cancelled) return;
        if (!resp?.success) {
          setNotesError(resp?.message || 'Failed to save model notes.');
          return;
        }
        lastSavedNotesRef.current = current;
      } catch (e) {
        if (cancelled) return;
        setNotesError(e?.detail || e?.message || 'Failed to save model notes.');
      } finally {
        if (!cancelled) setNotesSaving(false);
      }
    }, 650);

    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [modelNotes, distillationEventId, clientId, role]);

  // Generative tasks show teacher setup (and optionally student setup) instead
  // of the flat "… Used" sections. Student setup is only shown when this
  // distillation was fired with "Include prompt in training & evaluation".
  const showGenerativeSetup = isGenerativeTask(taskType);
  const showStudentSetup =
    showGenerativeSetup &&
    details?.distillation_event?.include_instruction_in_training === true;
  // Student overrides fall back to the snapshotted teacher fields — mirrors
  // the backend's resolved_student_* inheritance at training time.
  const promptUsed =
    details?.distillation_event?.generative_student_prompt ||
    details?.distillation_event?.instruction ||
    '';
  const systemPromptUsed =
    details?.distillation_event?.generative_student_instructions ||
    details?.distillation_event?.teacher_system_prompt ||
    '';
  const schemaUsed = details?.distillation_event?.generative_schema != null
    ? (details.distillation_event.generative_schema || '')
    : (details?.distillation_event?.teacher_schema || '');
  const trainingConfig = details?.training?.training_params || null;
  const trainingConfigText = useMemo(
    () => (trainingConfig ? safeJsonStringify(trainingConfig) : ''),
    [trainingConfig]
  );

  const dataSnapshot = useMemo(
    () => {
      const snapshotTaskType = details?.distillation_event?.task_type || null;

      const live = details?.live_counts || {};
      const labelCounts = live.label_names_count || details?.distillation_event?.label_names_count || {};
      const entityCounts = live.entity_names_count || details?.distillation_event?.entity_names_count || {};
      const relationCounts = live.relation_names_count || details?.distillation_event?.relation_names_count || {};

      const labelEntries = Object.entries(labelCounts || {});
      const parsedLabels = labelEntries.map(([label, v]) => {
        const total = Number(v?.total) || 0;
        const validated = Number(v?.validated) || 0;
        return { label, total, validated };
      });

      const entityEntries = Object.entries(entityCounts || {});
      const parsedEntities = entityEntries.map(([label, v]) => {
        const total = Number(v?.total) || 0;
        const validated = Number(v?.validated) || 0;
        return { label, total, validated };
      });

      const relationEntries = Object.entries(relationCounts || {});
      const parsedRelations = relationEntries.map(([label, v]) => {
        const total = Number(v?.total) || 0;
        const validated = Number(v?.validated) || 0;
        return { label, total, validated };
      });

      // Total Examples must reflect the number of training *records* (the examples
      // the model actually trained + validated on), not the sum of per-label /
      // per-entity occurrences. Summing over-counts any task where a single record
      // can carry multiple labels/entities (NER, multi-label classification).
      // train + validation record counts are the authoritative snapshot and exclude
      // the held-out benchmark (which is shown on the Benchmarks tab). The uploaded
      // example count is NOT used here: when a benchmark is auto-split from the
      // training data it also counts the benchmark records. Legacy events that
      // predate the split counts fall back to the (imperfect) label/entity sums,
      // then to the uploaded example count.
      const trainRecords = Number(details?.distillation_event?.total_train_records);
      const validationRecords = Number(details?.distillation_event?.total_validation_records) || 0;
      const trainingRecords =
        Number.isFinite(trainRecords) && trainRecords > 0 ? trainRecords + validationRecords : 0;
      const uploadedExamples = Number(details?.distillation_event?.total_examples_count) || 0;
      const legacyLabelTotal = parsedLabels.reduce((acc, x) => acc + x.total, 0);
      const legacyEntityTotal = parsedEntities.reduce((acc, x) => acc + x.total, 0);
      const totalExamplesNumber =
        trainingRecords > 0
          ? trainingRecords
          : legacyLabelTotal > 0
            ? legacyLabelTotal
            : legacyEntityTotal > 0
              ? legacyEntityTotal
              : uploadedExamples;

      const totalLabelsNumber = parsedLabels.length;
      const totalEntitiesNumber = parsedEntities.length;
      const totalRelationsNumber = parsedRelations.length;

      const sortByTotalThenLabel = (a, b) => {
        if (b.total !== a.total) return b.total - a.total;
        return String(a.label).localeCompare(String(b.label));
      };

      const reviewDepth = [...parsedLabels].sort(sortByTotalThenLabel);
      const entityReviewDepth = [...parsedEntities].sort(sortByTotalThenLabel);
      const relationsReviewDepth = [...parsedRelations].sort(sortByTotalThenLabel);

      const datasets = details?.distillation_event?.datasets || [];
      const datasetOrigins = details?.distillation_event?.dataset_origins || [];
      const datasetNamesFromOrigins = Array.isArray(datasetOrigins)
        ? datasetOrigins.map((x) => x?.name).filter(Boolean)
        : [];

      // Show only dataset origins (ignore benchmarks origins in UI).
      const files =
        [...datasets].filter(Boolean).join(', ') || datasetNamesFromOrigins.join(', ');

      // Surface the raw counts so the Data tab can explain to the customer why
      // "Total Examples" (training records) can be lower than the number of rows
      // they uploaded (duplicates + benchmark-overlapping rows are dropped, and a
      // benchmark may be held out from the dataset).
      const usesTrainingRecordCount = trainingRecords > 0 && totalExamplesNumber === trainingRecords;

      return {
        taskType: snapshotTaskType,
        totalExamples: totalExamplesNumber ? totalExamplesNumber.toLocaleString() : '-',
        totalExamplesValue: totalExamplesNumber || 0,
        uploadedExamplesValue: uploadedExamples || 0,
        usesTrainingRecordCount,
        dataReduction: details?.distillation_event?.data_reduction || null,
        totalLabels: totalLabelsNumber ? totalLabelsNumber.toLocaleString() : '-',
        totalEntities: totalEntitiesNumber ? totalEntitiesNumber.toLocaleString() : '-',
        totalRelations: totalRelationsNumber ? totalRelationsNumber.toLocaleString() : '-',
        datasetOrigins: Array.isArray(datasetOrigins) ? datasetOrigins : [],
        files: files || '-',
        reviewDepth,
        entityReviewDepth,
        relationsReviewDepth,
      };
    },
    [details?.distillation_event, details?.live_counts]
  );

  const showSubObjectiveInMeta = useMemo(
    () => shouldShowTrainingSubObjective(version, version?.usecase_name),
    [version]
  );

  const headerMeta = useMemo(() => {
    const baseModel = details?.training?.base_model_name || '-';
    const teacherModel = details?.distillation_event?.teacher_model || '-';
    const lastUpdated =
      details?.distillation_event?.last_update_datetime ||
      details?.training?.finished_datetime ||
      details?.training?.created_datetime ||
      null;

    const isMultilabel = details?.distillation_event?.is_multilabel;
    const classifySpeakers = details?.distillation_event?.classify_speakers;
    const fullTaskLabel = getTaskTypeLabel(taskType, isMultilabel, classifySpeakers);
    const isClassification = taskType === TaskType.classification;
    const taskTypeDisplay = isClassification ? 'Classification' : fullTaskLabel;
    const taskTypeTooltip = isClassification ? fullTaskLabel.replace(/^Classification\s*\((.+)\)$/, '$1') : null;

    return { baseModel, teacherModel, lastUpdated, taskTypeDisplay, taskTypeTooltip };
  }, [details, taskType]);

  const summaryMeta = useMemo(() => {
    const usecaseGoal = details?.distillation_event?.usecase_goal || '';
    const taskLanguagesRaw = details?.distillation_event?.task_languages || [];
    const taskLanguages = Array.isArray(taskLanguagesRaw) ? taskLanguagesRaw : [];
    const comparisonModeDisplay =
      details?.distillation_event?.comparison_mode_display ||
      details?.distillation_event?.comparisonModeDisplay ||
      '';
    const raw = details?.distillation_event?.key_changes || [];
    const keyChanges = Array.isArray(raw) ? raw : [];
    const sortedKeyChanges = [...keyChanges].sort((a, b) => {
      const ta = Date.parse(a?.created_datetime ?? '') || 0;
      const tb = Date.parse(b?.created_datetime ?? '') || 0;
      if (tb !== ta) return tb - ta;
      const sa = String(a?.display_name || a?.description || '');
      const sb = String(b?.display_name || b?.description || '');
      return sa.localeCompare(sb);
    });
    const modelSizeGb = details?.distillation_event?.model_size_gb;
    const modelParamsCount = details?.distillation_event?.model_parameters_count;
    let modelSizeDisplay = '-';
    if (modelSizeGb != null && modelParamsCount != null) {
      const paramsStr = modelParamsCount >= 1e9
        ? `${(modelParamsCount / 1e9).toFixed(1)}B`
        : modelParamsCount >= 1e6
          ? `${(modelParamsCount / 1e6).toFixed(0)}M`
          : modelParamsCount >= 1e3
            ? `${(modelParamsCount / 1e3).toFixed(0)}K`
            : String(modelParamsCount);
      modelSizeDisplay = `${modelSizeGb.toFixed(1)}GB (${paramsStr} parameters)`;
    } else if (modelSizeGb != null) {
      modelSizeDisplay = `${modelSizeGb.toFixed(1)}GB`;
    } else if (modelParamsCount != null) {
      const paramsStr = modelParamsCount >= 1e9
        ? `${(modelParamsCount / 1e9).toFixed(1)}B`
        : modelParamsCount >= 1e6
          ? `${(modelParamsCount / 1e6).toFixed(0)}M`
          : modelParamsCount >= 1e3
            ? `${(modelParamsCount / 1e3).toFixed(0)}K`
            : String(modelParamsCount);
      modelSizeDisplay = `${paramsStr} parameters`;
    }
    // Business goals snapshotted onto the distillation event at train time
    // (read-only here; edited from Task Settings).
    const businessGoalPrioritiesRaw = details?.distillation_event?.business_goal_priorities || [];
    const businessGoalPriorities = Array.isArray(businessGoalPrioritiesRaw)
      ? businessGoalPrioritiesRaw
      : [];
    const constraintsRaw = details?.distillation_event?.constraints || [];
    const constraints = Array.isArray(constraintsRaw) ? constraintsRaw : [];

    return {
      usecaseGoal,
      taskLanguages,
      comparisonModeDisplay,
      keyChanges: sortedKeyChanges,
      modelSizeDisplay,
      businessGoalPriorities,
      constraints,
    };
  }, [details]);

  const infraMeta = useMemo(() => {
    const de = details?.distillation_event || {};
    const tr = details?.training || {};

    const instanceType = de.instance_type || '-';
    const acceleratorName = de.accelerator;
    const acceleratorCount = de.accelerator_count;
    const vram = de.video_memory_gib;
    const gpus =
      typeof acceleratorCount === 'number' || acceleratorName
        ? [
            typeof acceleratorCount === 'number' ? `${acceleratorCount}x` : null,
            acceleratorName || 'GPU',
            typeof vram === 'number' ? `(${vram} GB)` : null,
          ]
            .filter(Boolean)
            .join(' ')
        : '-';

    const start = tr.created_datetime || de.created_datetime || null;
    const end = tr.finished_datetime || null;
    const durationMs =
      start && end
        ? new Date(end).getTime() - new Date(start).getTime()
        : null;
    const trainingTime =
      durationMs != null && durationMs >= 0
        ? formatDurationMs(durationMs)
        : '-';

    const trainCost = de.actually_training_cost_usd;
    const evalCost = de.actually_evaluation_cost_usd;
    const totalCost =
      de.actually_training_and_evaluation_cost_usd ??
      (typeof trainCost === 'number' && typeof evalCost === 'number'
        ? trainCost + evalCost
        : null);
    const costEstimate =
      typeof totalCost === 'number' ? `$${totalCost.toFixed(2)}` : '-';

    const predictionStrategy = isVllmTaskType({ task_type: de.task_type })
      ? null
      : getTrainedPredictionStrategyDisplayLabel(tr);

    return { instanceType, gpus, trainingTime, costEstimate, predictionStrategy };
  }, [details]);

  const trainingParams = useMemo(() => {
    // Generative tasks have no training-device / metric-optimisation controls.
    if (isGenerativeTask(taskType)) return null;

    const de = details?.distillation_event || {};

    const device = String(de.hardware_device || '').toLowerCase();
    const trainingDevice =
      device === 'cpu' ? 'CPU' : device === 'gpu' ? 'GPU' : '-';

    const evalArgs = de.model_evaluation_arguments || {};
    const metric = evalArgs.optimization_metric;
    const beta = evalArgs.f_score_metric_beta_coefficient;
    const metricLabelMap = {
      recall: 'Recall',
      precision: 'Precision',
      accuracy: 'Accuracy',
      f1_score: 'F1 score',
    };
    let metricOptimization = '-';
    if (metric === 'f_beta_score') {
      metricOptimization =
        typeof beta === 'number' ? `F score (β = ${beta})` : 'F score';
    } else if (metric) {
      metricOptimization = metricLabelMap[metric] || metric;
    }

    // "Optimize on": the label the metric above was computed for, or "All
    // labels". Single-label classification only (null hides the row).
    const optimizeOn = optimizeOnDisplay(de, taskType);

    // Prediction strategy is classification-only (hidden for NER).
    const isClassification = taskType === TaskType.classification;
    let predictionStrategy = null;
    if (isClassification) {
      const tr = details?.training || {};
      // The resolved strategy this model was actually trained with.
      const actualLabel = getTrainedPredictionStrategyDisplayLabel(tr);
      // An explicit Train-page choice persists on the training; "Automatic"
      // collapses to null on save, so its absence means the user let the
      // hyperparameter search pick the strategy.
      predictionStrategy = tr.prediction_strategy
        ? actualLabel
        : `Automatic (${actualLabel})`;
    }

    // Confidence-based rejection ("won't predict when not confident") is a
    // single-label classification toggle only. Surface whether it was enabled
    // for this run and, if so, which label (if any) acts as the "Other"
    // fallback when the model is not confident.
    let lowConfidenceHandling = null;
    let lowConfidenceTooltip = null;
    if (isClassification && !de.is_multilabel) {
      // A configured "Fallback" label can be a falsy-looking string like "0", so
      // check presence explicitly rather than truthiness.
      const rejectionLabel = de.rejection_label_name;
      const hasRejectionLabel =
        rejectionLabel != null && String(rejectionLabel) !== '';
      if (de.confidence_rejection_enabled) {
        if (hasRejectionLabel) {
          lowConfidenceHandling = `Returns "${rejectionLabel}" when no label meets the confidence threshold.`;
          lowConfidenceTooltip = `When the model is not confident enough to predict one of the primary labels, it returns "${rejectionLabel}" as a fallback category.`;
        } else {
          lowConfidenceHandling =
            'Returns no prediction when no label meets the confidence threshold.';
        }
      } else {
        lowConfidenceHandling = 'Always returns the highest-confidence label.';
      }
    }

    return {
      trainingDevice,
      metricOptimization,
      optimizeOn,
      predictionStrategy,
      lowConfidenceHandling,
      lowConfidenceTooltip,
    };
  }, [details, taskType]);

  // "V{n}" label for the shown version — used by the Data tab's train-set
  // download filename (same convention as the Versions Diff modal).
  const shownVersionNumber = details?.distillation_event?.version ?? version?.version;
  const dataTabVersionLabel =
    shownVersionNumber != null && shownVersionNumber !== '' ? `V${shownVersionNumber}` : null;

  const isNotesDirty = (modelNotes ?? '') !== (lastSavedNotesRef.current ?? '');

  // Live "still training" indicator for the shown version. Prefer the
  // realtime websocket entry (`trainStatuses`), falling back to the status
  // snapshot returned by model-details (page reload mid-training).
  const liveTrainEntry = distillationEventId ? trainStatuses?.[distillationEventId] : null;
  const currentTrainingStatus = liveTrainEntry?.status || details?.training?.status || null;
  const isTrainingInProgress =
    Boolean(currentTrainingStatus) && !FINAL_TRAINING_STATUSES.includes(currentTrainingStatus);

  return (
    <div style={{ position: 'relative' }}>
      {loading ? (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.6)',
            zIndex: 10,
          }}
        >
          <Spinner size="large" />
        </div>
      ) : null}
      <div style={loading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
        <ModelDetailsTabsRegion
          sideTabs={sideTabs}
          activeId={activeTab}
          onChange={setActiveTab}
        >
          {/* Shared header — version selector — shown on every tab. */}
          {headerExtra}

          {/* Live indicator while this version is still training — same
              progress-bar + status-tag pair as the training progress card. */}
          {isTrainingInProgress && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 15,
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <Text variant="base-semibold" style={{ whiteSpace: 'nowrap' }}>
                Training in Progress
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Spark ProgressBar caps itself at 234px; match that width so
                    the status tag sits right next to the bar instead of being
                    pushed to the far edge by a stretched wrapper. */}
                <div style={{ width: 234 }}>
                  <TrainingProgress
                    distillationEventId={distillationEventId}
                    initialProgress={liveTrainEntry?.progress || 0}
                    initialStatus={currentTrainingStatus}
                    usecaseId={details?.distillation_event?.llm_usecase_id}
                    version={details?.distillation_event?.version}
                  />
                </div>
                {/* TagFeedback has no size prop — scale it down slightly. */}
                <div style={{ transform: 'scale(0.85)', transformOrigin: 'center left' }}>
                  <TrainingStatus
                    distillationEventId={distillationEventId}
                    initialStatus={currentTrainingStatus}
                    initialErrorDetails={liveTrainEntry?.error_details}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'summary' && (
                <VersionSummarySection
                  showSubObjectiveInMeta={showSubObjectiveInMeta}
                  version={{
                    ...version,
                    trained_on_label_agreements: Boolean(
                      version?.trained_on_label_agreements
                      || details?.training?.trained_on_label_agreements
                    ),
                  }}
                  headerMeta={headerMeta}
                  summaryMeta={summaryMeta}
                  showGenerativeSetup={showGenerativeSetup}
                  showStudentSetup={showStudentSetup}
                  distillationEvent={details?.distillation_event}
                  systemPromptUsed={systemPromptUsed}
                  promptUsed={promptUsed}
                  schemaUsed={schemaUsed}
                  modelNotes={modelNotes}
                  onModelNotesChange={setModelNotes}
                  notesSaving={notesSaving}
                  notesError={notesError}
                  isNotesDirty={isNotesDirty}
                  role={role}
                />
          )}
          {activeTab === 'benchmarks' && (
                <ModelDetailsBenchmarksTab
                  benchmarksEntries={details?.benchmarks_entries || []}
                  taskType={details?.distillation_event?.task_type}
                  selectedBenchmarkEventId={selectedBenchmarkEventId}
                  onSelectBenchmark={onSelectBenchmark}
                  onDownload={
                    onDownloadBenchmark && version
                      ? (benchmarkRow) =>
                        onDownloadBenchmark({
                          ...version,
                          distillation_event_id: benchmarkRow.distillation_event_id,
                          version: benchmarkRow.version,
                        })
                      : undefined
                  }
                />
          )}
          {activeTab === 'data' && (
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)' }}>
                  <ModelDetailsDataTab
                    snapshot={dataSnapshot}
                    clientId={clientId}
                    distillationEventId={distillationEventId}
                    versionLabel={dataTabVersionLabel}
                    taskName={version?.usecase_name}
                  />
                </div>
          )}
          {activeTab === 'training' && (
                /* minHeight (not height) so the tab — and the white card around
                   it — can grow past the viewport when the Training
                   Configuration content is tall. */
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 220px)' }}>
                  <ModelDetailsTrainingRunInfrastructure
                    instanceType={infraMeta.instanceType}
                    gpus={infraMeta.gpus}
                    trainingTime={infraMeta.trainingTime}
                    costEstimate={infraMeta.costEstimate}
                    trainingParams={trainingParams}
                  />

                  {Array.isArray(details?.training?.considered_base_models) &&
                    details.training.considered_base_models.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <Text
                          variant="heading-3-semibold"
                          as="h3"
                          style={{
                            marginTop: 0,
                            marginBottom: 18,
                            fontWeight: 600,
                            color: '#111827',
                          }}
                        >
                          Base models considered
                        </Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 26 }}>
                          {details.training.considered_base_models.map((name) => (
                            <TagGeneral key={name} label={name} />
                          ))}
                        </div>
                        <div style={{ height: 1, background: '#E5E7EB', width: '100%', margin: '10px 0 24px' }} />
                      </div>
                    )}

                  <ModelDetailsCodeSection
                    title="Training Configuration"
                    text={trainingConfigText}
                    copyText={trainingConfigText}
                    minHeight={180}
                    flexGrow
                  />
                </div>
          )}
        </ModelDetailsTabsRegion>
      </div>
      {error ? (
        <div style={{ marginTop: 12 }}>
          <AlertBanner
            feedback="error"
            showButton={false}
            showCloseButton={false}
            text={
              <span>
                <b>Unable to load model details</b>
                <br />
                {error}
              </span>
            }
          />
        </div>
      ) : null}
    </div>
  );
}
